import Base from "../../../Base/views/Base";
import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  IconButton,
  Tooltip,
  Backdrop,
  CircularProgress,
  Skeleton,
} from "@mui/material";

import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditNoteIcon from "@mui/icons-material/EditNote";
import SaveIcon from "@mui/icons-material/Save";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import GroupIcon from "@mui/icons-material/Group";
import Quill from "../../../Base/views/Quill";
import history from "../../../../../history";
import {
  GetEcmsGuidelineHelper,
  UpdateECMSGuidelinesHelper,
} from "../../helper/EcmsHelper";
import { useSelector } from "react-redux";
import { getUserData, isAdminOwner } from "../../../Base/helper/baseFunctions";

const EcmsLandingPage = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [guidelines, setGuidelines] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const userData = getUserData();
  const { atsModuleData } = useSelector(
    (state) => state?.RoleAndAccessReducer?.ATSModuleReducer
  );
  const myEmployeeAccess = atsModuleData?.find(
    (item) => item.module_name === "My Employee"
  );
  const leaveAccess = atsModuleData?.find(
    (item) => item.module_name === "Leave"
  );
  const stepItems = [
    {
      title: "Step 1: Submit Request",
      desc: "Exit request can be initiated by Self / Admin",
      icon: <AssignmentTurnedInIcon fontSize="large" color="primary" />,
    },
    {
      title: "Step 2: Manager Review",
      desc: "Continuous process of clearance completion",
      icon: <SupervisorAccountIcon fontSize="large" color="warning" />,
    },
    {
      title: "Step 3: Final Clearance",
      desc: "Full and final settlement on completion of all expected clearances",
      icon: <CheckCircleIcon fontSize="large" color="success" />,
    },
  ];
  const buttonItems = [
    {
      label: "Submit Resignation",
      desc: "Submit and track your resignation.",
      icon: <ExitToAppIcon />,
      path: "/exit-management/my-ecms",
      show: leaveAccess && userData?.core_hr_user_type !== "Owner",
    },
    {
      label: "Admin View",
      desc: "Manage all exit requests.",
      icon: <AdminPanelSettingsIcon />,
      path: "/exit-management/admin-view",
      show: myEmployeeAccess,
    },
    {
      label: "Manager View",
      desc: "Approve/Reject clearances and view reportees.",
      icon: <GroupIcon />,
      path: "/exit-management/manager-view",
      show: myEmployeeAccess,
    },
  ];

  const handleUpdateGuideline = () => {
    UpdateECMSGuidelinesHelper({ ecms_guideline: guidelines }).then((res) => {
      if (res?.data?.statuscode === 200) {
        setGuidelines(res?.data?.data?.[0]?.fnf_guideline);
      }
    });
  };
  useEffect(() => {
    setIsLoading(true);
    GetEcmsGuidelineHelper().then((res) => {
      setIsLoading(false);
      if (res?.data?.statuscode === 200) {
        setGuidelines(res?.data?.data?.[0]?.fnf_guideline);
      }
    });
  }, []);
  return (
    <Base>
      <Box
        height="calc(100vh - 65px)"
        p={1}
        pr={1.5}
        pb={"20px"}
        sx={{ overflowY: "auto" }}
      >
        {/* Exit Process Overview */}
        <Box textAlign="center" mb={2}>
          <Typography variant="h6" gutterBottom>
            Exit Process Overview
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={"-4px"}>
            Quick Overview of Exit Management flow
          </Typography>
        </Box>

        <Grid container spacing={2}>
          {stepItems?.map((step, idx) => (
            <Grid item xs={12} sm={4} key={idx}>
              <Card
                variant="outlined"
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <CardContent
                  sx={{ flexGrow: 1, textAlign: "center", px: 1.5, py: 2 }}
                >
                  {step.icon}
                  <Typography variant="subtitle1" fontWeight="bold" mt={1}>
                    {step.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: 1.2 }}
                  >
                    {step.desc}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={2} mt={0}>
          <Grid item xs={12} md={4}>
            <Card variant="outlined">
              <CardContent sx={{ pt: 3, pb: 0 }}>
                <Box display="flex" flexDirection="column" gap={2}>
                  {buttonItems?.map(
                    (btn, idx) =>
                      btn?.show && (
                        <Box key={idx}>
                          <Box px={5}>
                            <Button
                              variant="contained"
                              className="mpl-primary-btn"
                              size="medium"
                              fullWidth
                              startIcon={btn.icon}
                              sx={{ mb: 1 }}
                              onClick={() => history.push(btn?.path)}
                            >
                              {btn?.label}
                            </Button>
                          </Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            textAlign="center"
                          >
                            {btn?.desc}
                          </Typography>
                        </Box>
                      )
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Right: Guidelines */}
          <Grid item xs={12} md={8}>
            <Card variant="outlined" sx={{ minHeight: "257px" }}>
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mt={"-16px"}
                  height={40}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    color="warning.main"
                  >
                    Important Guideline
                  </Typography>
                 {isAdminOwner () && <Tooltip
                    title={isEditing ? "Save Guidelines" : "Edit Guidelines"}
                    arrow
                  >
                    <IconButton
                      color={isEditing ? "primary" : "default"}
                      onClick={() => setIsEditing(!isEditing)}
                    >
                      {isEditing ? (
                        <SaveIcon
                          fontSize="medium"
                          onClick={handleUpdateGuideline}
                        />
                      ) : (
                        <EditNoteIcon fontSize="medium" />
                      )}
                    </IconButton>
                  </Tooltip>}
                </Box>

                {isLoading ? (
                  <Box mt={1}>
                    <Skeleton width="90%" />
                    <Skeleton width="80%" />
                    <Skeleton width="95%" />
                    <Skeleton width="70%" />
                    <Skeleton width="80%" />
                    <Skeleton width="70%" />
                  </Box>
                ) : isEditing ? (
                  <Quill
                    value={guidelines}
                    setValue={setGuidelines}
                    readonly={false}
                    hideHeading={true}
                    Toolbar={true}
                  />
                ) : guidelines ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    dangerouslySetInnerHTML={{ __html: guidelines }}
                    sx={{
                      "& ul": { margin: 0, paddingLeft: 2 },
                      "& li": { marginBottom: "4px" },
                    }}
                  />
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: "center", mt: "10%" }}
                  >
                    No guideline data available.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Base>
  );
};

export default EcmsLandingPage;
